import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  UserIcon,
  MapIcon,
  LockClosedIcon,
  ArrowRightOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { useGetCurrentUserQuery } from "../usersApiSlice";
import { useSendLogoutMutation } from "../../auth/authApiSlice";
import UpdateUserInfo from "./UpdateUserInfo";
import UpdatePassword from "./UpdatePassword";
import UserBookings from "./UserBookings";
import LoadingPage from "../../../shared/LoadingPage";

type Tab = "profile" | "bookings" | "password";

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: "profile", label: "Profile", icon: <UserIcon className="h-5 w-5" /> },
  {
    id: "bookings",
    label: "My Bookings",
    icon: <MapIcon className="h-5 w-5" />,
  },
  {
    id: "password",
    label: "Password",
    icon: <LockClosedIcon className="h-5 w-5" />,
  },
];

const AccountOverview = () => {
  const { data, isLoading, isError } = useGetCurrentUserQuery("");
  const [logout] = useSendLogoutMutation();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("profile");

  if (isLoading) return <LoadingPage />;

  if (isError || !data) {
    return (
      <div className="mx-auto max-w-[1180px] px-6 pt-28 md:px-12">
        <p className="text-hard">Oops! Failed to load your account.</p>
      </div>
    );
  }

  const user = data.data.data;
  const avatar = `${process.env.REACT_APP_API_URL}/img/users/${
    user.photo ? user.photo : "default.jpg"
  }`;

  const handleLogout = async () => {
    try {
      await logout("").unwrap();
    } catch {
      /* clear session locally regardless */
    }
    navigate("/");
    navigate(0);
  };

  return (
    <div className="mx-auto max-w-[1180px] px-6 pb-[70px] pt-24 md:px-12 md:pt-28">
      <h1 className="mb-1.5 font-serif text-[36px] font-normal tracking-[-0.02em] text-ink md:text-[44px]">
        Your account
      </h1>
      <p className="mb-8 text-[16px] text-muted">
        Manage your profile, trips, and security.
      </p>

      <div className="grid gap-8 md:grid-cols-[248px_1fr] md:items-start">
        {/* SIDEBAR */}
        <aside className="rounded-[20px] bg-white p-[22px] shadow-[0_14px_30px_-22px_rgba(0,0,0,0.25)] md:sticky md:top-[90px]">
          <div className="mb-4 flex items-center gap-3 border-b border-line-100 px-1.5 pb-[18px]">
            <img
              src={avatar}
              alt={user.name}
              crossOrigin="anonymous"
              className="h-[50px] w-[50px] flex-none rounded-full object-cover"
            />
            <div className="min-w-0">
              <div className="truncate text-[15px] font-bold text-ink">
                {user.name}
              </div>
              <div className="truncate text-[12.5px] text-muted-300">
                {user.email}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-[14.5px] font-semibold transition-colors ${
                  tab === t.id
                    ? "bg-secondary-400 text-white"
                    : "text-muted hover:bg-[#ece7da]"
                }`}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </div>

          <div className="mt-4 border-t border-line-100 pt-3.5">
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-[14.5px] font-semibold text-hard transition-colors hover:bg-hard/5"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              Log out
            </button>
          </div>
        </aside>

        {/* PANEL */}
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
        >
          {tab === "profile" && <UpdateUserInfo user={user} />}
          {tab === "bookings" && <UserBookings />}
          {tab === "password" && <UpdatePassword />}
        </motion.div>
      </div>
    </div>
  );
};

export default AccountOverview;
